import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { canonicalBook, includesVerse, loadCommentary } from '../utils/reformationCommentary';
import './ReformationCommentary.css';

const AUTHORS = { calvin: 'カルバン', luther: 'ルター' };
const KINDS = { commentary: '注解', chapter: '章全体の解説', sermon: 'この箇所の説教', related: '関連する記述' };

function CommentaryText({ item, work, book, chapter, verse }) {
  const [state, setState] = useState({ loading: true });
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    loadCommentary(item.chunk, controller.signal).then(data => {
      if (!data.entries?.[item.id]) throw new Error('Missing entry');
      if (!controller.signal.aborted) setState({ record: data.entries[item.id], language: data.language || 'en' });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ error: true });
    });
    return () => controller.abort();
  }, [item.chunk, item.id]);

  if (state.loading) return <p role="status">注解を読み込んでいます…</p>;
  if (state.error) return <p role="alert">本文を読み込めませんでした。別の項目を選ぶか、開き直してください。</p>;
  const record = state.record;
  const segments = record.segments?.filter(segment => includesVerse(segment.targets, book, chapter, verse)) || [];
  const text = !expanded && segments.length ? segments.map(segment => segment.text).join('\n\n') : record.text;
  const sectionId = record.source.elementId?.split('-p')[0];
  const url = work.url.replace(/\.html$/, sectionId && /^[ivxlcdm]+(?:\.[ivxlcdm]+)*$/.test(sectionId) ? `.${sectionId}.html` : '.html') +
    (record.source.elementId ? `#${encodeURIComponent(record.source.elementId)}` : '');
  return <article className="reformation-article">
    <div className="reformation-article__meta">{AUTHORS[item.author]} · {KINDS[item.kind]} <span>{state.language === 'ja' ? '日本語訳（機械翻訳）' : '英語原文'}</span></div>
    {item.kind === 'related' && <p className="reformation-note">この聖句への参照を含む段落です。編集者の説明や他者の引用を含む場合があります。</p>}
    <div className="reformation-prose" lang={state.language}>{text.split(/\n\s*\n/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
    <footer className="reformation-source">
      {segments.length > 0 && <button className="reformation-expand" onClick={() => setExpanded(value => !value)}>
        {expanded ? 'この節に戻る' : '前後を読む'}
      </button>}
      <a href={url} target="_blank" rel="noopener noreferrer" title={`${work.title}${work.translator ? ` / ${work.translator}` : ''} / ${record.source.elementId || `行 ${record.source.startLine}–${record.source.endLine}`}`}>出典 ↗</a>
    </footer>
  </article>;
}

export default function ReformationCommentary({ book, bookName, chapter, verse, verseText, onClose }) {
  const dialog = useRef(null);
  const [state, setState] = useState({ loading: true });
  const [author, setAuthor] = useState('');
  const [selected, setSelected] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const previous = document.activeElement;
    const element = dialog.current;
    element.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = overflow;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true });
    Promise.all([
      loadCommentary(`index/${canonicalBook(book)}/${chapter}.json`, controller.signal),
      loadCommentary('catalog.json', controller.signal),
    ]).then(([index, catalog]) => {
      if (!Array.isArray(index.entries) || !catalog.works) throw new Error('Invalid index');
      const entries = index.entries.filter(entry => entry.verses.includes(verse))
        .sort((a, b) => (a.kind === 'related') - (b.kind === 'related'));
      if (!controller.signal.aborted) setState({ entries, works: catalog.works });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ error: true });
    });
    return () => controller.abort();
  }, [book, chapter, verse, attempt]);

  const available = state.entries || [];
  const effectiveAuthor = available.some(item => item.author === author) ? author : available[0]?.author;
  const entries = available.filter(item => item.author === effectiveAuthor);
  const active = entries.find(item => item.id === selected) || entries[0];
  const activeIndex = active ? entries.indexOf(active) : 0;
  return createPortal(<dialog ref={dialog} className="reformation-dialog" aria-labelledby="reformation-title"
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => { if (event.target === dialog.current) onClose(); }}>
    <div className="reformation-shell">
      <header className="reformation-header">
        <h2 id="reformation-title">{bookName} <span>{chapter}:{verse}</span></h2>
        <button className="reformation-close" onClick={onClose} aria-label="注解を閉じる" autoFocus>×</button>
      </header>
      {verseText && <blockquote className="reformation-verse">{verseText}</blockquote>}
      <div className="reformation-authors" aria-label="著者で絞り込み">
        {Object.entries(AUTHORS).filter(([id]) => available.some(item => item.author === id)).map(([id, label]) => <button key={id} aria-pressed={effectiveAuthor === id} onClick={() => { setAuthor(id); setSelected(''); }}>
          {label}
        </button>)}
      </div>
      {state.loading ? <p className="reformation-status" role="status">この節の注解を探しています…</p> : state.error ?
        <div className="reformation-status" role="alert"><p>注解を読み込めませんでした。</p><button onClick={() => setAttempt(value => value + 1)}>再読み込み</button></div> :
        !entries.length ? <p className="reformation-status">この節に対応する注解・関連記述は、現在の収録資料では見つかっていません。</p> :
          <div className="reformation-body">
            <div className="reformation-reader" key={active.id}>
              <CommentaryText item={active} work={state.works[active.workId]} book={book} chapter={chapter} verse={verse} />
            </div>
            {entries.length > 1 && <nav className="reformation-pager" aria-label="ほかの解説">
              <button aria-label="前の解説" disabled={activeIndex === 0} onClick={() => setSelected(entries[activeIndex - 1].id)}>‹</button>
              <span>{activeIndex + 1} / {entries.length}</span>
              <button aria-label="次の解説" disabled={activeIndex === entries.length - 1} onClick={() => setSelected(entries[activeIndex + 1].id)}>›</button>
            </nav>}
          </div>}
    </div>
  </dialog>, document.body);
}
